import * as CommonActions from 'embed/actions'
import * as Actions from 'embed/actions/application'
import {
  Application,
  ApplicationFields,
  Field,
  FieldData,
  FieldDataValue,
  FieldKey,
  isUuid,
  LifeInsuranceQuestionFreeform,
  LifeInsuranceQuestionGroups,
  MappedLifeInsuranceQuestions,
} from 'embed/constants'
import { QuestionMachineConfig } from 'embed/constants/life-insurance'
import Session from 'embed/services/Session'
import { mapLifeInsuranceQuestions } from 'embed/utils/life-insurance'
import { Fields, getInvalidFields } from 'embed/constants/fields'
import { LifeInsuranceAnswerOption, LifeInsuranceQuestionType, Product } from '@evenfinancial/even-ts-static'
import { DeepReadonly } from 'utility-types'
import { AvailableDemandPartner } from 'embed/actions'

export interface LifeInsuranceApplication extends Application {
  routerQuestions?: {
    questions?: MappedLifeInsuranceQuestions
    groups?: LifeInsuranceQuestionGroups
    machineConfig?: QuestionMachineConfig
  }
  availableDemandPartners?: DeepReadonly<AvailableDemandPartner[]>
}

export type ApplicationState = DeepReadonly<{ [P in Exclude<Product, Product.LifeInsurance>]?: Application }> &
  DeepReadonly<{ [Product.LifeInsurance]: LifeInsuranceApplication }>

type ActionTypes = Actions.ActionTypes | CommonActions.ActionTypes

export const initialApplicationState = {
  [Product.CreditCard]: {
    fields: {},
    isValid: true,
  },
  [Product.LifeInsurance]: {
    fields: {},
    isValid: true,
    routerQuestions: { groups: [], questions: {} },
  },
  [Product.Loan]: {
    fields: {},
    isValid: true,
  },
  [Product.Mortgage]: {
    fields: {},
    isValid: true,
  },
  [Product.Deposits]: {
    fields: {},
    isValid: true,
  },
}

export const findField = (fieldKey: FieldKey): Field | undefined =>
  Object.values(Fields).find((field) => field.key === fieldKey)

export const getOption = (answerOption: LifeInsuranceAnswerOption) => answerOption.answerValue

export const isFreeform = (questionType: LifeInsuranceQuestionType) => LifeInsuranceQuestionFreeform.has(questionType)

export const getRouterQuestionIsValid = (
  fieldKey: FieldKey,
  value: FieldDataValue,
  state: ApplicationState,
): boolean => {
  const question = state.life_insurance.routerQuestions?.questions?.[fieldKey]
  if (question === undefined || (question.answerOptions === undefined && !isFreeform(question.questionType))) {
    return false
  }

  if (isFreeform(question.questionType)) {
    return Array.isArray(value) && value.length > 0
  }

  const answerOptionsSet: Set<FieldDataValue> = new Set(question.answerOptions?.map(getOption))

  if (Array.isArray(value)) {
    if (value.length === 0) {
      return !!question.allowNoSelection
    }
    return value.every((currentValue) => answerOptionsSet.has(currentValue))
  }

  return answerOptionsSet.has(value)
}

const isRegisteredFieldValid = (
  action: Actions.ApplicationFieldRegistered | Actions.ApplicationFieldValidate,
  state: ApplicationState,
  value: FieldDataValue,
): boolean => {
  const isRouterQuestionAction = isUuid(action.key)
  const field = findField(action.key)
  return isRouterQuestionAction
    ? getRouterQuestionIsValid(action.key, value, state)
    : field !== undefined && field.validate(value, action.validationData)
}

const getChangedFields = (state: ApplicationState, action: CommonActions.FiltersChanged): ApplicationFields => {
  const fields = { ...state[action.product]?.fields }
  for (const filter of Object.keys(action.filters)) {
    if (filter) {
      const field = findField(filter)
      const value = action.filters[filter]
      const options = action.options?.[filter] ?? {}

      if (field !== undefined && field.canChangeValue(value, fields[filter], options)) {
        const isValid = field.validate(value, action.validationData)
        fields[filter] = { isValid, shouldShowValidationStatus: true, value, ...options }
      }
    }
  }
  return fields
}

const getUpdatedFieldPlaidInfo = (currentFieldData: FieldData, action: Actions.ApplicationFieldChanged): FieldData => {
  const options = action.options ?? {}
  const updatedField = { ...currentFieldData, value: action.value, ...options }
  if (action.populatedByPlaid) {
    // If the field was populated by plaid, indicate that
    updatedField.populatedByPlaid = action.populatedByPlaid
  } else if (updatedField.populatedByPlaid) {
    // If the field was previously populated by plaid, but was later changed, indicate that
    updatedField.populatedByPlaidThenChanged = true
  }
  return updatedField
}

const setSessionReturningLead = (action: Actions.ApplicationFieldChanged): void => {
  const isBeingPopulatedByTheUser = action.options?.dataSource?.id === undefined
  if (isBeingPopulatedByTheUser && !Session.isReturningLead) {
    Session.returningLead = new Date().getTime().toString()
  }
}

const getApplicationFieldChangedState = (
  state: ApplicationState,
  action: Actions.ApplicationFieldChanged,
): ApplicationState => {
  const field = findField(action.key)
  const isRouterQuestionAction = isUuid(action.key)
  if (field === undefined && !isRouterQuestionAction) {
    return state
  }

  const currentFieldData = state[action.product]?.fields?.[action.key] ?? {}
  const options = action.options ?? {}

  // Read only fields can't be changed beyond their initial value
  // If action.key is a uuid, then the question is a router question and so it can change value
  if (!isRouterQuestionAction && !field?.canChangeValue(action.value, currentFieldData, options)) {
    return state
  }
  setSessionReturningLead(action)

  const updatedField = getUpdatedFieldPlaidInfo(currentFieldData, action)

  const nextState = { ...state }
  nextState[action.product] = {
    ...state[action.product],
    fields: {
      ...state[action.product]?.fields,
      [action.key]: updatedField,
    },
  }
  return nextState
}

const getApplicationFieldRegisteredState = (
  state: ApplicationState,
  action: Actions.ApplicationFieldRegistered,
): ApplicationState => {
  const field = findField(action.key)
  const isRouterQuestionAction = isUuid(action.key)
  if (field === undefined && !isRouterQuestionAction) {
    return state
  }

  let isFormValid = state[action.product]?.isValid
  const value = state?.[action.product]?.fields?.[action.key]?.value

  // If the newly registered field is invalid, invalidate the form
  const registeredFieldValid = isRegisteredFieldValid(action, state, value)

  if (!registeredFieldValid) {
    isFormValid = false
  }

  const nextState = { ...state }

  nextState[action.product] = {
    ...state[action.product],
    fields: {
      ...state[action.product]?.fields,
      [action.key]: { isValid: registeredFieldValid, ...state[action.product]?.fields?.[action.key] },
    },
    // if newly registered field is required, invalidate form
    isValid: isFormValid,
  }
  return nextState
}

const getApplicationFieldValidateState = (
  state: ApplicationState,
  action: Actions.ApplicationFieldValidate,
): ApplicationState => {
  const field = findField(action.key)
  const isRouterQuestionAction = isUuid(action.key)
  if (field === undefined && !isRouterQuestionAction) {
    return state
  }
  const value = action.value

  const isValid = isRegisteredFieldValid(action, state, value)
  const fieldData: FieldData = { ...state[action.product]?.fields?.[action.key], isValid }
  if (action.shouldShowValidationStatus === true) {
    fieldData.shouldShowValidationStatus = true
  }

  const nextState = { ...state }
  nextState[action.product] = {
    ...state[action.product],
    fields: {
      ...state[action.product]?.fields,
      [action.key]: fieldData,
    },
  }

  // Update the validation of the application overall
  const invalidFields = getInvalidFields(nextState[action.product] ?? {}, action.product)
  nextState[action.product] = {
    ...nextState[action.product],
    isValid: invalidFields.length === 0,
  }
  return nextState
}

const reducer = (state: ApplicationState = initialApplicationState, action: ActionTypes) => {
  switch (action.type) {
    case Actions.ActionTypeKeys.APPLICATION_FIELD_BLURRED: {
      const nextState = { ...state }
      nextState[action.product] = {
        ...state[action.product],
        fields: {
          ...state[action.product]?.fields,
          [action.key]: { ...state[action.product]?.fields?.[action.key], shouldShowValidationStatus: true },
        },
      }
      return nextState
    }
    case Actions.ActionTypeKeys.APPLICATION_FIELD_CHANGED: {
      const nextState = getApplicationFieldChangedState(state, action)
      return nextState
    }
    case Actions.ActionTypeKeys.APPLICATION_FIELD_LOADING_END: {
      return {
        ...state,
        [action.product]: {
          ...state[action.product],
          fields: {
            ...state[action.product]?.fields,
            [action.key]: { ...state[action.product]?.fields?.[action.key], isLoading: false },
          },
        },
      }
    }
    case Actions.ActionTypeKeys.APPLICATION_FIELD_LOADING_START: {
      return {
        ...state,
        [action.product]: {
          ...state[action.product],
          fields: {
            ...state[action.product]?.fields,
            [action.key]: { ...state[action.product]?.fields?.[action.key], isLoading: true },
          },
        },
      }
    }
    case Actions.ActionTypeKeys.APPLICATION_FIELD_REGISTERED: {
      const nextState = getApplicationFieldRegisteredState(state, action)
      return nextState
    }
    case Actions.ActionTypeKeys.APPLICATION_FIELD_SHOW_VALIDATION_STATUS: {
      const nextState = { ...state }
      nextState[action.product] = {
        ...state[action.product],
        fields: {
          ...state[action.product]?.fields,
          [action.key]: { ...state[action.product]?.fields?.[action.key], shouldShowValidationStatus: action.show },
        },
      }

      return nextState
    }
    case Actions.ActionTypeKeys.APPLICATION_FIELD_UNREGISTERED: {
      const nextState = { ...state }
      if (nextState[action.product]?.fields?.[action.key]) {
        // @TODO: update params to satisfy linter requirement or comment to why this needs to stay as is
        const { [action.key]: keyValue, ...withoutKey } = { ...nextState[action.product]?.fields }
        return {
          ...nextState,
          [action.product]: {
            ...nextState[action.product],
            fields: { ...withoutKey },
          },
        }
      }
      return nextState
    }
    case Actions.ActionTypeKeys.APPLICATION_FIELD_VALIDATE: {
      const nextState = getApplicationFieldValidateState(state, action)
      return nextState
    }
    case Actions.ActionTypeKeys.APPLICATION_UPDATE_VALIDATION_STATUS: {
      // Update the validation of the application overall
      const invalidFields = getInvalidFields(state[action.product] ?? {}, action.product)
      return { ...state, [action.product]: { ...state[action.product], isValid: invalidFields.length === 0 } }
    }
    case CommonActions.ActionTypeKeys.FILTERS_CHANGED: {
      // Any changes made to filters should also apply to application fields
      const nextState = { ...state }

      const fields = getChangedFields(state, action)

      nextState[action.product] = { ...nextState[action.product], fields }
      return nextState
    }
    case CommonActions.ActionTypeKeys.FETCH_ROUTER_QUESTIONS_SUCCESS: {
      const { questions, groups } = action.data
      return {
        ...state,
        [Product.LifeInsurance]: {
          ...state[Product.LifeInsurance],
          routerQuestions: {
            ...state[Product.LifeInsurance].routerQuestions,
            questions: mapLifeInsuranceQuestions(questions),
            groups,
          },
        },
      }
    }
    case CommonActions.ActionTypeKeys.FETCH_AVAILABLE_DEMAND_PARTNERS_SUCCESS: {
      return {
        ...state,
        [action.product]: {
          ...state[action.product],
          availableDemandPartners: action.data,
        },
      }
    }

    case Actions.ActionTypeKeys.APPLICATION_QUESTION_MACHINE_CONFIG_CALCULATED: {
      return {
        ...state,
        [Product.LifeInsurance]: {
          ...state[Product.LifeInsurance],
          routerQuestions: {
            ...state[Product.LifeInsurance].routerQuestions,
            machineConfig: action.machineConfig,
          },
        },
      }
    }
    default:
      return state
  }
}

export default reducer
